import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Mail, Phone, Bell, KeyRound } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import api from '@/lib/api';
import { sendPasswordResetEmail } from '@/lib/auth';
import Card from '@/components/ui/Card';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';

interface EmailPrefs {
  categories: {
    product_updates: boolean;
    growing_tips: boolean;
    promotions: boolean;
    seasonal: boolean;
  };
  frequency: 'all' | 'reduced' | 'minimal';
}

const DEFAULT_PREFS: EmailPrefs = {
  categories: { product_updates: true, growing_tips: true, promotions: true, seasonal: true },
  frequency: 'all',
};

interface ProfileForm {
  firstName: string;
  lastName: string;
  phone: string;
  newsletter: boolean;
  smsOptIn: boolean;
  emailPreferences: EmailPrefs;
}

export default function Profile() {
  const { t } = useTranslation('account');
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const addToast = useUIStore((s) => s.addToast);

  const [form, setForm] = useState<ProfileForm>({
    firstName: '',
    lastName: '',
    phone: '',
    newsletter: false,
    smsOptIn: false,
    emailPreferences: DEFAULT_PREFS,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);

  async function handlePasswordReset() {
    if (!user?.email) return;
    setIsSendingReset(true);
    try {
      await sendPasswordResetEmail(user.email);
      addToast(t('profile.passwordResetSent'), 'success');
    } catch (err: any) {
      addToast(err?.message || t('profile.passwordResetFailed'), 'error');
    } finally {
      setIsSendingReset(false);
    }
  }

  const emailCategories = useMemo(
    () => [
      {
        key: 'product_updates' as const,
        label: t('profile.newsletter.categories.productUpdatesLabel'),
        desc: t('profile.newsletter.categories.productUpdatesDesc'),
      },
      {
        key: 'growing_tips' as const,
        label: t('profile.newsletter.categories.growingTipsLabel'),
        desc: t('profile.newsletter.categories.growingTipsDesc'),
      },
      {
        key: 'promotions' as const,
        label: t('profile.newsletter.categories.promotionsLabel'),
        desc: t('profile.newsletter.categories.promotionsDesc'),
      },
      {
        key: 'seasonal' as const,
        label: t('profile.newsletter.categories.seasonalLabel'),
        desc: t('profile.newsletter.categories.seasonalDesc'),
      },
    ],
    [t],
  );

  const frequencyOptions = useMemo(
    () => [
      { value: 'all' as const, label: t('profile.newsletter.frequencies.all') },
      { value: 'reduced' as const, label: t('profile.newsletter.frequencies.reduced') },
      { value: 'minimal' as const, label: t('profile.newsletter.frequencies.minimal') },
    ],
    [t],
  );

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: (user as any).phone || '',
        newsletter: (user as any).newsletter ?? false,
        smsOptIn: (user as any).sms_opt_in ?? false,
        emailPreferences: (user as any).email_preferences || DEFAULT_PREFS,
      });
    }
  }, [user]);

  function updateField(field: keyof ProfileForm, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.firstName.trim()) newErrors.firstName = t('profile.errors.firstNameRequired');
    if (!form.lastName.trim()) newErrors.lastName = t('profile.errors.lastNameRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      const updated = await api.put<{
        id: string;
        email: string;
        first_name: string;
        last_name: string;
        phone: string;
        newsletter_subscribed: boolean;
        email_preferences: EmailPrefs;
        role: string;
        avatar_url: string | null;
      }>('/user/profile', {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        phone: form.phone.trim(),
        newsletter_subscribed: form.newsletter,
        sms_opt_in: form.smsOptIn,
        email_preferences: form.emailPreferences,
      });

      // Update auth store with the fresh data
      if (user) {
        setUser({
          ...user,
          firstName: updated.first_name ?? form.firstName.trim(),
          lastName: updated.last_name ?? form.lastName.trim(),
        });
      }

      addToast(t('profile.savedToast'), 'success');
      setIsDirty(false);
    } catch (err: any) {
      addToast(err.message || t('profile.saveFailedToast'), 'error');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div>
      <h2 className="text-xl font-display font-bold text-ink mb-6">
        {t('profile.heading')}
      </h2>

      <Card className="max-w-2xl">
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5">
            {/* Avatar area */}
            <div className="flex items-center gap-4 pb-5 border-b border-line">
              <div className="h-16 w-16 rounded-full bg-sage-100 flex items-center justify-center shrink-0">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <User
                    className="h-8 w-8 text-sage-700"
                    aria-hidden="true"
                  />
                )}
              </div>
              <div>
                <p className="font-semibold text-ink">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-sm text-ink-muted">{user?.email}</p>
              </div>
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1">
                <span className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4 text-ink-muted" aria-hidden="true" />
                  {t('profile.emailLabel')}
                </span>
              </label>
              <input
                type="email"
                value={user?.email || ''}
                readOnly
                disabled
                className="w-full rounded-lg border border-line-strong px-3 py-2 text-sm text-ink-muted bg-surface-soft cursor-not-allowed"
                aria-label={t('profile.emailReadOnlyAriaLabel')}
              />
              <p className="mt-1 text-xs text-ink-muted">
                {t('profile.emailReadOnlyHelp')}
              </p>
            </div>

            {/* Name fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label={t('profile.firstName')}
                value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                error={errors.firstName}
                required
                autoComplete="given-name"
              />
              <Input
                label={t('profile.lastName')}
                value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                error={errors.lastName}
                required
                autoComplete="family-name"
              />
            </div>

            {/* Phone */}
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Phone className="h-4 w-4 text-ink-muted" aria-hidden="true" />
                <label
                  htmlFor="profile-phone"
                  className="text-sm font-medium text-ink-soft"
                >
                  {t('profile.phone')}
                </label>
              </div>
              <Input
                id="profile-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                helperText={t('profile.phoneHelp')}
                autoComplete="tel"
              />
            </div>

            {/* SMS consent */}
            <div className="pt-2 border-t border-line">
              <div className="flex items-start gap-3">
                <div className="flex h-5 items-center">
                  <input
                    id="sms-toggle"
                    type="checkbox"
                    checked={form.smsOptIn}
                    disabled={!form.phone.trim()}
                    onChange={(e) => updateField('smsOptIn', e.target.checked)}
                    className="h-4 w-4 rounded border-line-strong text-sage-600 focus:ring-sage-500 focus:ring-2 focus:ring-offset-2"
                  />
                </div>
                <div>
                  <label
                    htmlFor="sms-toggle"
                    className="text-sm font-medium text-ink flex items-center gap-1.5 cursor-pointer"
                  >
                    <Phone className="h-4 w-4 text-ink-muted" aria-hidden="true" />
                    {t('profile.sms.heading')}
                  </label>
                  <p className="text-sm text-ink-muted mt-0.5">
                    {form.phone.trim() ? t('profile.sms.description') : t('profile.sms.noPhone')}
                  </p>
                </div>
              </div>
            </div>

            {/* Newsletter preferences */}
            <div className="pt-2 border-t border-line space-y-4">
              {/* Master toggle */}
              <div className="flex items-start gap-3">
                <div className="flex h-5 items-center">
                  <input
                    id="newsletter-toggle"
                    type="checkbox"
                    checked={form.newsletter}
                    onChange={(e) => updateField('newsletter', e.target.checked)}
                    className="h-4 w-4 rounded border-line-strong text-sage-600 focus:ring-sage-500 focus:ring-2 focus:ring-offset-2"
                  />
                </div>
                <div>
                  <label
                    htmlFor="newsletter-toggle"
                    className="text-sm font-medium text-ink flex items-center gap-1.5 cursor-pointer"
                  >
                    <Bell className="h-4 w-4 text-ink-muted" aria-hidden="true" />
                    {t('profile.newsletter.heading')}
                  </label>
                  <p className="text-sm text-ink-muted mt-0.5">
                    {t('profile.newsletter.description')}
                  </p>
                </div>
              </div>

              {/* Category & frequency preferences (shown when subscribed) */}
              {form.newsletter && (
                <div className="ml-7 space-y-4">
                  {/* Categories */}
                  <div>
                    <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide mb-2">{t('profile.newsletter.categoriesHeading')}</p>
                    <div className="space-y-2">
                      {emailCategories.map((cat) => (
                        <label key={cat.key} className="flex items-center gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.emailPreferences.categories[cat.key]}
                            onChange={() => {
                              setForm((prev) => ({
                                ...prev,
                                emailPreferences: {
                                  ...prev.emailPreferences,
                                  categories: {
                                    ...prev.emailPreferences.categories,
                                    [cat.key]: !prev.emailPreferences.categories[cat.key],
                                  },
                                },
                              }));
                              setIsDirty(true);
                            }}
                            className="h-3.5 w-3.5 rounded border-line-strong text-sage-600 focus:ring-sage-500"
                          />
                          <span className="text-sm text-ink">{cat.label}</span>
                          <span className="text-xs text-ink-muted">{cat.desc}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Frequency */}
                  <div>
                    <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide mb-2">{t('profile.newsletter.frequencyHeading')}</p>
                    <div className="space-y-1.5">
                      {frequencyOptions.map((freq) => (
                        <label key={freq.value} className="flex items-center gap-2.5 cursor-pointer">
                          <input
                            type="radio"
                            name="email-frequency"
                            checked={form.emailPreferences.frequency === freq.value}
                            onChange={() => {
                              setForm((prev) => ({
                                ...prev,
                                emailPreferences: { ...prev.emailPreferences, frequency: freq.value },
                              }));
                              setIsDirty(true);
                            }}
                            className="h-3.5 w-3.5 border-line-strong text-sage-600 focus:ring-sage-500"
                          />
                          <span className="text-sm text-ink">{freq.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-line">
            <Button
              type="submit"
              isLoading={isSaving}
              disabled={!isDirty && !isSaving}
            >
              {t('profile.saveButton')}
            </Button>
          </div>
        </form>
      </Card>

      {/* Password — uses the SuperTokens reset-by-email flow (no in-app change API) */}
      <Card className="max-w-2xl mt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-ink flex items-center gap-1.5">
              <KeyRound className="h-4 w-4 text-ink-muted" aria-hidden="true" />
              {t('profile.passwordHeading')}
            </h3>
            <p className="text-sm text-ink-muted mt-0.5">{t('profile.passwordResetHelp')}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handlePasswordReset}
            isLoading={isSendingReset}
            className="shrink-0"
          >
            {t('profile.passwordResetButton')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
